import { useState, useEffect, useRef } from "react";
import { CheckCircle2, Circle, Loader2, XCircle, Upload, AlertTriangle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PUSH_STEPS } from "@/server/sync/types";
import type { PushStep } from "@/server/sync/types";

interface PushToProductionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collectionId: string;
  collectionName: string;
}

type DialogState = "confirm" | "progress" | "complete" | "error";

interface StepState {
  step: PushStep;
  label: string;
  status: "pending" | "active" | "done";
  detail?: string;
}

export function PushToProductionDialog({
  open,
  onOpenChange,
  collectionId,
  collectionName,
}: PushToProductionDialogProps) {
  const [state, setState] = useState<DialogState>("confirm");
  const [steps, setSteps] = useState<StepState[]>([]);
  const [progressPercent, setProgressPercent] = useState(0);
  const [resultStats, setResultStats] = useState<Record<string, number> | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const utils = trpc.useUtils();

  const { data: syncStatus } = trpc.collections.getSyncStatus.useQuery(
    { collectionId },
    { enabled: open },
  );

  const pushMutation = trpc.collections.pushToProduction.useMutation({
    onSuccess: (data) => {
      connectSSE(data.pushId);
    },
    onError: (err) => {
      setErrorMessage(err.message);
      setState("error");
    },
  });

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setState("confirm");
      setSteps([]);
      setProgressPercent(0);
      setResultStats(null);
      setErrorMessage(null);
    } else {
      // Cleanup SSE on close
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    }
  }, [open]);

  function connectSSE(pushId: string) {
    const es = new EventSource(`/api/push-events/${pushId}`);
    eventSourceRef.current = es;

    // Initialize steps
    setSteps(
      PUSH_STEPS.map((s) => ({
        step: s.step,
        label: s.label,
        status: "pending",
      })),
    );

    es.addEventListener("push_progress", (e) => {
      const data = JSON.parse(e.data);
      const { step, stepIndex, totalSteps, detail } = data;

      if (step === "completed") {
        setState("complete");
        es.close();
        // Invalidate queries to refresh sync status
        utils.collections.getById.invalidate({ id: collectionId });
        utils.collections.list.invalidate();
        utils.collections.getSyncStatus.invalidate({ collectionId });
        return;
      }

      if (step === "failed") {
        setErrorMessage(detail ?? "Unknown error");
        setState("error");
        es.close();
        return;
      }

      // Update steps
      setSteps((prev) =>
        prev.map((s, i) => {
          if (i < stepIndex) return { ...s, status: "done" };
          if (i === stepIndex) return { ...s, status: "active", detail };
          return { ...s, status: "pending" };
        }),
      );

      setProgressPercent(Math.round(((stepIndex + 1) / totalSteps) * 100));
    });

    es.onerror = () => {
      // SSE may close naturally on completion
      if (state === "progress") {
        setErrorMessage("Connection lost during push");
        setState("error");
      }
      es.close();
    };
  }

  function handlePush() {
    setState("progress");
    pushMutation.mutate({ collectionId });
  }

  function handleClose() {
    if (state === "progress") return; // Don't close during push
    onOpenChange(false);
  }

  const newCount = syncStatus ? syncStatus.total - syncStatus.pushed : 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {state === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Push to Production
              </DialogTitle>
              <DialogDescription>
                Push "{collectionName}" to the production environment.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total locations</span>
                <span className="font-medium">{syncStatus?.total ?? "..."}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Already pushed</span>
                <span className="font-medium">{syncStatus?.pushed ?? 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">New to push</span>
                <span className="font-medium text-primary">{newCount}</span>
              </div>
            </div>

            {newCount === 0 && (
              <div className="flex items-start gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/50 rounded-md p-3">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  All locations already synced. Re-running will push any new
                  PILs, photos, and sport courts on existing locations.
                </span>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handlePush} disabled={pushMutation.isPending}>
                {pushMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-1.5" />
                )}
                {newCount > 0
                  ? `Push ${newCount} location${newCount !== 1 ? "s" : ""}`
                  : "Sync mapping data"}
              </Button>
            </DialogFooter>
          </>
        )}

        {state === "progress" && (
          <>
            <DialogHeader>
              <DialogTitle>Pushing to Production...</DialogTitle>
              <DialogDescription>
                Please don't close this window while the push is in progress.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <Progress value={progressPercent} className="h-2" />
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {steps.map((s) => (
                  <div key={s.step} className="flex items-start gap-2 text-sm">
                    {s.status === "done" && (
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    )}
                    {s.status === "active" && (
                      <Loader2 className="h-4 w-4 text-primary animate-spin mt-0.5 shrink-0" />
                    )}
                    {s.status === "pending" && (
                      <Circle className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <span className={s.status === "pending" ? "text-muted-foreground/60" : ""}>
                        {s.label}
                      </span>
                      {s.detail && s.status === "active" && (
                        <p className="text-xs text-muted-foreground truncate">{s.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {state === "complete" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle2 className="h-5 w-5" />
                Push Complete
              </DialogTitle>
              <DialogDescription>
                All new locations have been pushed to production.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2 py-4">
              {steps
                .filter((s) => s.status === "done")
                .map((s) => (
                  <div key={s.step} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                    {s.label}
                  </div>
                ))}
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </>
        )}

        {state === "error" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <XCircle className="h-5 w-5" />
                Push Failed
              </DialogTitle>
              <DialogDescription>
                An error occurred while pushing to production.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <p className="text-sm text-destructive bg-destructive/10 rounded-md p-3">
                {errorMessage}
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setState("confirm");
                  setErrorMessage(null);
                }}
              >
                Retry
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
